import { Immutable } from "immer";

import type { ImageChooserBlock } from "@reactivated";

/**
 * An unset RichText field serializes to an empty-but-TRUTHY
 * `<div class="rich-text"></div>`, so a plain `!!value` truthy check would
 * falsely enable optional content (e.g. the competition-compare-table 4th
 * column) -- widening markup while the matching cells stay empty. Gate on REAL
 * content: strip tags/whitespace; an `<img>` counts as content.
 */
export const hasRichTextContent = (
    html: string | null | undefined,
): boolean => {
    if (!html) {
        return false;
    }
    if (/<img[\s/>]/i.test(html)) {
        return true;
    }
    return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

/**
 * Resolve the best `src` for a serialized ``ImageChooserBlock``.
 *
 * The serializer inlines small images as a base64 ``imageData`` data-URI
 * (which is what the screenshot baselines contain) and otherwise exposes named
 * renditions. We prefer ``imageData`` when present, then fall back to the widest
 * rendition, then the raw ``url`` -- the same source image either way.
 */
export const getImageSrc = (image: Immutable<ImageChooserBlock>): string => {
    if (image.imageData) {
        return image.imageData;
    }
    const renditions = Object.values(image.renditions);
    if (renditions.length > 0) {
        return renditions.reduce((largest, current) =>
            current.width > largest.width ? current : largest,
        ).url;
    }
    return image.url;
};
